import * as React from 'react';

import type { CourseFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SpecializationEntitiesFragments';
import { getCourseFilterOptions } from 'bundles/unified-description-page-common/utils/getMockSpecializationReviews';

type CourseFilterableCourse = Pick<CourseFragment, 'id' | 'slug' | 'name'>;

/**
 * Shared course-filter-dropdown state for specialization/professional-certificate reviews
 * surfaces (the XDP reviews module and the standalone reviews page) -- defaults to the first
 * child course, deduped by slug, so both surfaces behave identically.
 *
 * The default is derived on every render rather than baked into useState's initial value: on the
 * standalone reviews page, `courses` is still empty on the first render (before the page-data
 * query resolves), so a lazy useState initializer would capture `undefined` forever and never
 * pick up the first course once data arrives.
 */
export const useSpecializationCourseFilter = <T extends CourseFilterableCourse>(courses?: Array<T> | null) => {
  const courseFilterOptions = getCourseFilterOptions(courses);
  const [selectedCourseSlugOverride, setSelectedCourseSlug] = React.useState<string | undefined>(undefined);
  const selectedCourseSlug = selectedCourseSlugOverride ?? courseFilterOptions[0]?.slug;
  const selectedCourseId = courses?.find((course) => course.slug === selectedCourseSlug)?.id;

  return { courseFilterOptions, selectedCourseSlug, setSelectedCourseSlug, selectedCourseId };
};
